import { useAuthStore } from '@/stores/authStore'

const API_BASE = import.meta.env.VITE_API_URL || ''

export interface AgentEvent {
  event_id: string
  event_type: 'task_created' | 'task_updated' | 'task_assigned' | 'timer_started' | 'timer_stopped' | 'time_logged' | 'notification_sent'
  firm_id: string
  source_user_id: string
  affected_user_ids: string[]
  payload: Record<string, unknown>
  timestamp: string
}

interface EventStreamCallbacks {
  onEvent: (event: AgentEvent) => void
  onConnected: () => void
  onDisconnected: () => void
  onError: (error: Error) => void
}

/**
 * Connect to the agent event stream SSE endpoint.
 * Returns a disconnect function.
 *
 * Uses fetch + ReadableStream (not EventSource) to support Bearer auth header.
 * Implements exponential backoff reconnection (1s -> 30s max).
 */
export function connectEventStream(callbacks: EventStreamCallbacks): () => void {
  let aborted = false
  let abortController: AbortController | null = null
  let reconnectDelay = 1000
  const MAX_RECONNECT_DELAY = 30000

  async function connect() {
    if (aborted) return

    let activeToken = useAuthStore.getState().token
    if (!activeToken) {
      const refreshed = await useAuthStore.getState().refreshAccessToken()
      if (refreshed) {
        activeToken = useAuthStore.getState().token!
      } else {
        callbacks.onError(new Error('Session expired'))
        return
      }
    }

    abortController = new AbortController()

    try {
      const response = await fetch(`${API_BASE}/api/v1/events/stream`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${activeToken}`,
          Accept: 'text/event-stream',
        },
        signal: abortController.signal,
      })

      if (response.status === 401) {
        const refreshed = await useAuthStore.getState().refreshAccessToken()
        if (refreshed && !aborted) {
          reconnectDelay = 1000
          connect()
        } else {
          callbacks.onError(new Error('Authentication failed'))
        }
        return
      }

      if (!response.ok) {
        throw new Error(`Event stream failed: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body')
      }

      reconnectDelay = 1000

      const decoder = new TextDecoder()
      let buffer = ''

      try {
        while (!aborted) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            const trimmed = line.trim()

            if (!trimmed || trimmed.startsWith(':')) continue

            if (!trimmed.startsWith('data: ')) continue

            const jsonStr = trimmed.slice(6)
            try {
              const data = JSON.parse(jsonStr)

              if (data.type === 'connected') {
                callbacks.onConnected()
                continue
              }

              if (data.event_type) {
                callbacks.onEvent(data as AgentEvent)
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    } catch (err: unknown) {
      if (aborted) return
      if (err instanceof DOMException && err.name === 'AbortError') return

      callbacks.onDisconnected()
      callbacks.onError(err instanceof Error ? err : new Error(String(err)))
    }

    if (!aborted) {
      callbacks.onDisconnected()
      setTimeout(() => {
        if (!aborted) connect()
      }, reconnectDelay)
      reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY)
    }
  }

  connect()

  return () => {
    aborted = true
    abortController?.abort()
  }
}
